import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useBalance } from 'wagmi';
import { useWalletUtils } from '@/hooks/useWalletUtils';
import { formatEther } from 'viem';
import { Droplets } from 'lucide-react';

interface WalletStatusProps {
  onShowNetworkModal?: () => void;
}

const MEZO_FAUCET_URL = 'https://faucet.test.mezo.org/';

export const WalletStatus = ({ onShowNetworkModal }: WalletStatusProps) => {
  const { isMezoTestnet, chainId } = useWalletUtils();
  const { address, isConnected } = useAccount();

  // Get BTC balance
  const { data: walletBalance, isLoading: isBalanceLoading } = useBalance({
    address: address,
    query: {
      enabled: !!address && isConnected,
    },
  });

  const balanceBTC = walletBalance ? parseFloat(formatEther(walletBalance.value)) : 0;
  const formattedBalance = balanceBTC.toFixed(4);

  return (
    <ConnectButton.Custom>
      {({
        account,
        chain,
        openAccountModal,
        openConnectModal,
        authenticationStatus,
        mounted,
      }) => {
        const ready = mounted && authenticationStatus !== 'loading';
        const connected = ready && account && chain;
        const isOnMezo = isMezoTestnet(chainId);

        return (
          <div className="flex items-center gap-2">
            {!connected ? (
              <button
                onClick={openConnectModal}
                className="flex items-center gap-3 px-5 py-3 rounded-full bg-yellow-400/20 border border-yellow-400/50 text-white font-title font-bold hover:bg-yellow-400/30 transition-all shadow-sm"
              >
                <span className="w-7 h-7 rounded-full bg-yellow-400/20 border border-yellow-300/50 flex items-center justify-center">
                  <i className="fa-solid fa-wallet text-yellow-300"></i>
                </span>
                <span className="tracking-wide">Connect Wallet</span>
                <span className="ml-1 inline-flex items-center text-yellow-300 animate-pulse">
                  <i className="fa-solid fa-arrow-right-long"></i>
                </span>
              </button>
            ) : (
              <div className="flex items-center gap-2">
                {/* Mezo Faucet Button - Only show on Mezo Testnet */}
                {isOnMezo && (
                  <a
                    href={MEZO_FAUCET_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center w-10 h-10 rounded-full bg-cyan-500/20 border border-cyan-400/50 text-cyan-300 hover:bg-cyan-500/30 hover:border-cyan-400/70 transition-all shadow-sm"
                    title="Get free testnet BTC from Mezo Faucet"
                  >
                    <Droplets className="w-5 h-5" />
                  </a>
                )}
                
                {/* BTC Balance - Shows only amount */}
                <button
                  onClick={openAccountModal}
                  className="px-3 py-1.5 rounded-full bg-white text-black border border-white/10 font-navbar flex items-center gap-1.5 hover:bg-white/90 transition-all"
                  title="View account details"
                >
                  <span className="text-xs font-bold">
                    {isBalanceLoading ? '...' : `${formattedBalance} BTC`}
                  </span>
                </button>
              </div>
            )}
          </div>
        );
      }}
    </ConnectButton.Custom>
  );
};
